import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SideSheetOptions } from 'src/app/shared/side-sheet/side-sheet.service';
import { TranslocoService } from '@ngneat/transloco';
import { ApolloError } from 'apollo-client';
import { Logger } from 'aws-amplify';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Observable, of } from 'rxjs';
import { finalize, switchMap, take, tap } from 'rxjs/operators';
import { Doctor, MassClinicPatient, NameFormat, QuestionnaireType } from 'src/API';
import { ErrorHandlerService } from 'src/app/core/api/error-handler.service';
import { PAMPRestAPIService } from 'src/app/core/api/pamp-rest-api.service';
import { safeJsonParse } from 'src/app/core/api/general.util';
import { canSendAsMultiQuestionnaire } from 'src/app/core/api/questionnaire-request-utils';
import {
  QuestionnaireRequest,
  QuestionnaireRequestService,
  SendQuestionnaireDialogData
} from 'src/app/core/api/questionnaire-request.service';
import { QuestionnaireSendBuilderService } from 'src/app/core/api/questionnaire-send-builder.service';
import { StaffService } from 'src/app/core/api/staff.service';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { IntakeFormService } from 'src/app/intake-form/intake-form.service';
import { v4 as uuid } from 'uuid';
import { Patient, PatientService } from '../../core/api/patient.service';
import {
  CheckboxAndInputComponent,
  CheckboxAndInputError
} from '../checkbox-and-input/checkbox-and-input.component';
import { ConfirmModalService } from '../confirm-modal/confirm-modal.service';
import { QuestionnairePickerComponent } from '../questionnaire-picker/questionnaire-picker.component';
import {
  PickerEntry,
  QuestionnairePickerSelection
} from '../questionnaire-picker/questionnaire-picker.model';
import { UpdateMassClinicPatientInput } from './../../../API';
import { ClinicSetupService } from './../../logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import {
  ClinicianPrefillPatch,
  consentFormRequiresClinicianInput
} from 'src/app/core/api/consent-form.util';
import { FormTemplateSchema } from 'src/app/form-template/form-template-model';
import {
  ConsentClinicianPrefillDialogComponent,
  ConsentClinicianPrefillDialogResult
} from '../consent-clinician-prefill-dialog/consent-clinician-prefill-dialog.component';
import {
  PampAssessmentTodoTemplate,
  pampAssessmentTodoTemplatesFromResponse
} from 'src/app/core/models/pamp-todo.model';
import { ConsentSendService } from 'src/app/core/api/consent-send.service';

export type SendQuestionnaireMatDialogRef = MatDialogRef<SendQuestionnaireDialogComponent>;

const MULTIPLE_QUESTIONNAIRE_PICKER_WIDTH = '58rem';

@Component({
  selector: 'csi-send-questionnaire-dialog',
  templateUrl: './send-questionnaire-dialog.component.html',
  styleUrls: ['./send-questionnaire-dialog.component.scss']
})
export class SendQuestionnaireDialogComponent implements OnInit, OnDestroy {
  readonly NameFormat = NameFormat;
  public formGroup: FormGroup;
  public questionnaireTypesFormGroup: FormGroup;
  public sendClicked = false;
  public sending = false;
  public atLeastOneCheckboxSelected = false;
  /** Includes Patient App todos, unlike atLeastOneCheckboxSelected. Drives the "nothing selected" error. */
  public atLeastOneItemSelected = false;
  public questionnairesToBeSent = [];
  private questionnaireRequestsSent = [];
  /** Friendly names of the Patient App todos the last dispatch actually created. */
  private pampTodoNamesSent: string[] = [];
  /** Prefills gathered before any create when multi-sending. Key = questionnaire type/schema key. */
  private clinicianPrefillsByType: {
    [questionnaireType: string]: ClinicianPrefillPatch | null;
  } = {};
  public questionnaireTypeToFriendlyNameMap: { [key: string]: string } = {};

  public languageFormControl = new FormControl();

  /** Every currently checked entry, in section order - drives the right pane review list. */
  public selectedItems: PickerEntry[] = [];

  readonly emailErrors: CheckboxAndInputError[] = [
    { errorType: 'email', displayMessage: 'Invalid Email Address' },
    { errorType: 'required', displayMessage: 'Email is required' }
  ];

  readonly mobileErrors: CheckboxAndInputError[] = [
    { errorType: 'required', displayMessage: 'Mobile phone is required' }
  ];

  @ViewChild('emailInputs', { static: true }) emailInputs: CheckboxAndInputComponent;
  @ViewChild('mobileInputs', { static: true }) mobileInputs: CheckboxAndInputComponent;
  /** Only rendered in multi-select mode, so every use has to null-check. */
  @ViewChild(QuestionnairePickerComponent, { static: false })
  picker: QuestionnairePickerComponent;

  private logger: Logger = new Logger('SendQuestionnaireDialogComponent');

  public intakeFormKeyToFriendlyNameMap: { [key: string]: string } = {};
  public intakeFormKeyToSchemaMap = {};
  public sendIntakeFormsEnabled = true;

  public consentFormKeyToFriendlyNameMap = {};
  public consentFormKeyToSchemaMap = {};

  public multiSendDisabled = false;
  public isPampTodosLoading = false;
  public pampTodosFormGroup: FormGroup = new FormGroup({});
  public pampTodoKeyToFriendlyNameMap: { [key: string]: string } = {};
  public showPatientAppCategory = false;
  public canManageOneClickQRESets = false;
  public saveOneClickQRESetDisabled = true;
  public selectedCount = 0;
  public sendButtonLabel = 'Send';
  public sendDisabled = false;

  constructor(
    public dialogRef: MatDialogRef<SendQuestionnaireDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SendQuestionnaireDialogData,
    public questionnaireRequestService: QuestionnaireRequestService,
    private errorHandlerService: ErrorHandlerService,
    private snackBar: MatSnackBar,
    public translocoService: TranslocoService,
    public clinicSetupService: ClinicSetupService,
    public staffService: StaffService,
    public intakeFormService: IntakeFormService,
    public patientService: PatientService,
    private consentSendService: ConsentSendService,
    private pampRestApiService: PAMPRestAPIService,
    public appStateService: AppStateService,
    private confirmModalService: ConfirmModalService,
    private questionnaireSendBuilderService: QuestionnaireSendBuilderService,
    private matDialog: MatDialog
  ) {
    this.formGroup = this.questionnaireRequestService.createFormGroup(data);
    if (data.multipleQuestionnairesSelectable) {
      this.questionnaireTypesFormGroup = new FormGroup({});
      this.sendDisabled = true;
    }
    const languageToSend =
      (this.staffService.staff && this.staffService.staff.preferredLanguage) ||
      this.translocoService.getDefaultLang();

    this.languageFormControl.setValue(languageToSend);

    const { CUSTOM, ...rest } = this.questionnaireRequestService.questionnaireTypeToFriendlyNameMap;
    this.questionnaireTypeToFriendlyNameMap = rest as { [key: string]: string };
  }

  /** Required by untilDestroyed(this), which hooks this method to complete its subscriptions. */
  ngOnDestroy() {}

  public static readonly sideSheetOptions: SideSheetOptions = {
    width: MULTIPLE_QUESTIONNAIRE_PICKER_WIDTH,
    isFillHeight: true
  };

  public static buildDialogData(
    questionnaireType: QuestionnaireType,
    patient: Partial<Patient>,
    massClinicPatient: MassClinicPatient = null,
    multipleQuestionnairesSelectable = false,
    isSideSheet = false,
    preselectedQuestionnaireKeys: string[] = []
  ): SendQuestionnaireDialogData {
    return {
      questionnaireType,
      patient,
      massClinicPatient,
      questionnaireRequest:
        massClinicPatient && (massClinicPatient.questionnaireRequest as QuestionnaireRequest),
      multipleQuestionnairesSelectable,
      preselectedQuestionnaireKeys,
      isSideSheet
    };
  }

  public static open(
    matDialogService: MatDialog,
    questionnaireType: QuestionnaireType,
    patient: Partial<Patient>,
    modalConfig: MatDialogConfig<never> = {},
    massClinicPatient: MassClinicPatient = null,
    multipleQuestionnairesSelectable = false
  ): SendQuestionnaireMatDialogRef {
    return matDialogService.open<SendQuestionnaireDialogComponent, SendQuestionnaireDialogData>(
      SendQuestionnaireDialogComponent,
      {
        maxWidth: '95vw',
        height: 'auto',
        ...(multipleQuestionnairesSelectable && { width: MULTIPLE_QUESTIONNAIRE_PICKER_WIDTH }),
        ...modalConfig,
        data: SendQuestionnaireDialogComponent.buildDialogData(
          questionnaireType,
          patient,
          massClinicPatient,
          multipleQuestionnairesSelectable
        )
      }
    );
  }

  ngOnInit() {
    if (this.mobileInputs) {
      this.mobileInputs.checkboxControl.setValue(false); // Text message should be false by default
    }
    if (this.emailInputs) {
      this.emailInputs.checkboxControl.setValue(!!this.emailFormControl.value);
    }
    this.initializeIntakeFormMaps();
    this.initializeConsentFormMaps();
    this.sendIntakeFormsEnabled = this.shouldEnableSendingIntakeForms();

    this.fetchPampAssessmentTodoTemplates();
  }

  /** The picker owns the selection; this dialog only mirrors what it needs for the Send state. */
  public onPickerSelectionChange(selection: QuestionnairePickerSelection) {
    this.selectedItems = selection.selectedItems;
    this.selectedCount = selection.selectedItems.length;
    this.multiSendDisabled = selection.multiSendDisabled;
    this.atLeastOneCheckboxSelected = selection.atLeastOneQuestionnaireSelected;
    this.atLeastOneItemSelected = selection.atLeastOneItemSelected;
    this.canManageOneClickQRESets = selection.canManageOneClickQRESets;
    this.saveOneClickQRESetDisabled = selection.saveOneClickQRESetDisabled;
    this.refreshSendButtonState();
  }

  public deselect(entry: PickerEntry) {
    if (this.picker) {
      this.picker.deselect(entry);
    }
  }

  public clearAllSelections() {
    if (this.picker) {
      this.picker.clearAllSelections();
    }
  }

  public saveOneClickQRESet() {
    if (this.picker) {
      this.picker.saveOneClickQRESet();
    }
  }

  public trackByEntryKey(_index: number, entry: PickerEntry): string {
    return entry.key;
  }

  private fetchPampAssessmentTodoTemplates(): void {
    const isPatientAppActive = this.appStateService.getIsPatientAppActive();
    const hasPatientAppId = !!(this.data && this.data.patient && this.data.patient.patientAppId);
    if (!isPatientAppActive || !hasPatientAppId) {
      return;
    }

    if (this.staffService.staff && this.staffService.staff.id && this.staffService.staff.clinic) {
      this.loadAssessmentTodosForStaff(this.staffService.staff);
    } else {
      this.staffService
        .getLoggedInStaff()
        .pipe(untilDestroyed(this))
        .subscribe(
          staff => {
            this.loadAssessmentTodosForStaff(staff);
          },
          error => console.error('Error fetching logged in staff for PAMP todos:', error)
        );
    }
  }

  private loadAssessmentTodosForStaff(staff: Doctor): void {
    if (!staff) {
      return;
    }
    const ownerId = staff.id || '';
    const clinicId = staff.clinic ? staff.clinic.id : '';
    if (ownerId && clinicId) {
      this.isPampTodosLoading = true;
      this.pampRestApiService
        .getPampTodos(ownerId, clinicId, 'assessment')
        .pipe(
          finalize(() => {
            this.isPampTodosLoading = false;
          }),
          untilDestroyed(this)
        )
        .subscribe(
          (
            todoTemplates: PampAssessmentTodoTemplate[] | { data?: PampAssessmentTodoTemplate[] }
          ) => {
            const templates = pampAssessmentTodoTemplatesFromResponse(todoTemplates);

            const groupControls: { [id: string]: FormControl } = {};
            const nameMap: { [id: string]: string } = {};
            templates.forEach(template => {
              const id = template && template.id;
              const name = template ? template.title || template.name || template.id : '';
              if (id) {
                groupControls[id] = new FormControl(false);
                nameMap[id] = name;
              }
            });
            this.pampTodosFormGroup = new FormGroup(groupControls);
            this.pampTodoKeyToFriendlyNameMap = nameMap;
            // The picker rebuilds itself off these inputs, so the Patient App chip and its
            // checkboxes appear now the todos have landed.
            this.showPatientAppCategory = templates.length > 0;
          },
          error => console.error('Error loading PAMP assessment todo templates:', error)
        );
    }
  }

  private dispatchSelectedPampTodoForPatient(): Observable<any> {
    if (!this.pampTodosFormGroup || !this.data || !this.data.patient) {
      return of(null);
    }

    const selectedTodoIds = Object.keys(this.pampTodosFormGroup.controls).filter(
      id => this.pampTodosFormGroup.controls[id].value === true
    );

    if (selectedTodoIds.length === 0) {
      return of(null);
    }

    const patient = this.data.patient;
    const patientId = patient.id;
    const clinicId =
      this.staffService.staff && this.staffService.staff.clinic
        ? this.staffService.staff.clinic.id
        : '';
    const ownerId = this.staffService.clinicOwnerId;
    const enrollmentCode = patient.patientAppId;
    const clinicExpiry =
      (this.clinicSetupService &&
        this.clinicSetupService.clinicSetupFormGroup &&
        this.clinicSetupService.clinicSetupFormGroup.value &&
        this.clinicSetupService.clinicSetupFormGroup.value.questionnaireExpiry) ||
      14;

    if (patientId && clinicId) {
      const payload = {
        patientId,
        clinicId,
        ownerId,
        enrollmentCode,
        expiresInDays: Number(clinicExpiry),
        data: selectedTodoIds
      };

      return this.pampRestApiService.createPampTodoForPatient(payload).pipe(
        tap(
          () => {
            this.pampTodoNamesSent = selectedTodoIds.map(
              id => this.pampTodoKeyToFriendlyNameMap[id] || id
            );
          },
          error => {
            console.error('Error creating Patient App todos for patient:', error);
          }
        )
      );
    } else {
      return of(null);
    }
  }

  private refreshSendButtonState() {
    this.sendDisabled = !!this.data.multipleQuestionnairesSelectable && this.selectedCount === 0;
    if (this.selectedCount > 0) {
      this.sendButtonLabel = this.translocoService.translate('Send ({{count}})', {
        count: this.selectedCount
      });
    } else {
      this.sendButtonLabel = this.translocoService.translate('Send');
    }
  }


  onSubmit() {
    if (this.sending || this.formGroup.invalid || this.sendDisabled) {
      return;
    }
    this.sendClicked = true;
    this.sending = true;

    const hasSelectedPampTodos =
      this.pampTodosFormGroup &&
      Object.keys(this.pampTodosFormGroup.controls).some(
        id => this.pampTodosFormGroup.controls[id].value === true
      );

    const hasSelectedQuestionnaires =
      !this.data.multipleQuestionnairesSelectable ||
      (this.data.multipleQuestionnairesSelectable && this.atLeastOneCheckboxSelected);

    if (!hasSelectedQuestionnaires && hasSelectedPampTodos) {
      this.dispatchSelectedPampTodoForPatient().subscribe(
        () => {
          this.sending = false;
          this.dialogRef.close();
          this.openSuccessSnackbar();
        },
        error => {
          this.sending = false;
        }
      );
      return;
    }

    if (this.data.multipleQuestionnairesSelectable && this.atLeastOneCheckboxSelected) {
      this.questionnairesToBeSent = [];
      this.clinicianPrefillsByType = {};
      Object.keys({
        ...this.questionnaireRequestService.questionnaireTypeToFriendlyNameMap,
        ...this.questionnaireRequestService.customQuestionnaireKeyToFriendlyNameMap,
        ...(this.sendIntakeFormsEnabled ? this.intakeFormKeyToFriendlyNameMap : {}),
        ...this.consentFormKeyToFriendlyNameMap
      }).forEach(key => {
        if (
          this.questionnaireTypesFormGroup.controls[key] &&
          this.questionnaireTypesFormGroup.controls[key].value === true
        ) {
          this.questionnairesToBeSent.push(key);
        }
      });

      const alreadyAcceptedConsentKeys = this.questionnairesToBeSent.filter(key =>
        this.hasAcceptedConsentForSchema(key)
      );
      if (alreadyAcceptedConsentKeys.length > 0) {
        this.confirmModalService
          .show(
            'Consent form already completed',
            'This patient has already accepted one or more of the selected consent forms. Send again anyway?',
            'Send again',
            'Cancel'
          )
          .subscribe(confirmed => {
            if (!confirmed) {
              this.abortPendingSend();
              return;
            }
            this.startSendingSelectedQuestionnaires();
          });
        return;
      }

      this.startSendingSelectedQuestionnaires();
    } else if (!this.data.multipleQuestionnairesSelectable) {
      if (!this.data.questionnaireRequest) {
        this.createQuestionnaireRequest();
        return;
      }
      this.updateQuestionnaireType();
    } else {
      this.sending = false;
    }
  }

  private startSendingSelectedQuestionnaires() {
    if (!this.data.questionnaireRequest && this.questionnairesToBeSent.length !== 0) {
      // Collect every required clinician prefill before creating any QRE, so Cancel
      // aborts the whole batch and non-prefill forms are never emailed mid-dialog.
      const typesNeedingPrefill = this.getTypesNeedingClinicianPrefill(this.questionnairesToBeSent);
      this.collectClinicianPrefills$(typesNeedingPrefill).subscribe(allPrefillsCompleted => {
        if (!allPrefillsCompleted) {
          this.abortPendingSend();
          return;
        }
        const questionnaireGroupId = uuid();
        this.createQuestionnaireRequest(
          this.questionnairesToBeSent.pop(),
          this.questionnairesToBeSent.length > 0 ? questionnaireGroupId : undefined
        );
      });
      return;
    }
    this.updateQuestionnaireType(this.questionnairesToBeSent.pop());
  }

  private abortPendingSend() {
    this.sending = false;
    this.questionnairesToBeSent = [];
    this.clinicianPrefillsByType = {};
  }

  private resolveSchemaForType(questionnaireType: string): FormTemplateSchema | undefined {
    return this.questionnaireSendBuilderService.resolveSchemaForKey(questionnaireType);
  }

  private getTypesNeedingClinicianPrefill(types: string[]): string[] {
    return types.filter(questionnaireType =>
      consentFormRequiresClinicianInput(this.resolveSchemaForType(questionnaireType))
    );
  }

  /**
   * Opens clinician prefill dialogs sequentially for each type that needs them.
   * Emits true only if every dialog is completed; false if any is cancelled.
   */
  private collectClinicianPrefills$(typesNeedingPrefill: string[], index = 0): Observable<boolean> {
    if (index === 0) {
      this.clinicianPrefillsByType = {};
    }
    if (index >= typesNeedingPrefill.length) {
      return of(true);
    }

    const questionnaireType = typesNeedingPrefill[index];
    const schema = this.resolveSchemaForType(questionnaireType);
    return ConsentClinicianPrefillDialogComponent.open(this.matDialog, {
      schema,
      defaultPractitionerId: this.getDefaultPractitionerId(),
      questionnaireDisplayName: this.getConsentPrefillDisplayName(questionnaireType, schema)
    })
      .afterClosed()
      .pipe(
        take(1),
        switchMap((result: ConsentClinicianPrefillDialogResult | null) => {
          if (!result) {
            return of(false);
          }
          this.clinicianPrefillsByType[questionnaireType] = result.clinicianPrefill || null;
          return this.collectClinicianPrefills$(typesNeedingPrefill, index + 1);
        })
      );
  }

  public get mobileFormControl(): AbstractControl {
    return this.formGroup.controls.mobile;
  }

  public get emailFormControl(): AbstractControl {
    return this.formGroup.controls.email;
  }

  initializeIntakeFormMaps() {
    this.intakeFormKeyToSchemaMap = this.questionnaireSendBuilderService.getIntakeFormKeyToSchemaMap();

    this.intakeFormKeyToFriendlyNameMap = Object.entries(this.intakeFormKeyToSchemaMap).reduce(
      (acc, [key, val]) => {
        acc[key] = val['name'];
        return acc;
      },
      {}
    );
  }

  initializeConsentFormMaps() {
    this.consentFormKeyToSchemaMap = this.consentSendService.getConsentFormKeyToSchemaMap();
    this.consentFormKeyToFriendlyNameMap = this.consentSendService.getConsentFormKeyToFriendlyNameMap();
  }

  hasAcceptedConsentForSchema(schemaKey: string): boolean {
    return this.consentSendService.hasAcceptedConsentForSchema(
      schemaKey,
      this.patientService.questionnaires
    );
  }

  shouldEnableSendingIntakeForms() {
    if (this.patientService.intakeForms && this.patientService.intakeForms.length > 0) {
      return false;
    }
    const intakeQuestionnaires = this.patientService.questionnaires.filter(questionnaire => {
      const config = safeJsonParse(questionnaire.config);
      return config && config.schema && config.schema.type === 'intake';
    });
    return intakeQuestionnaires.every(questionnaire =>
      this.questionnaireRequestService.isQuestionnaireExpired(questionnaire)
    );
  }

  private createQuestionnaireRequest(questionnaireType?: string, groupId?: string) {
    this.sending = true;
    if (questionnaireType) {
      this.formGroup.controls['type'].patchValue(questionnaireType);
    }

    const resolvedType =
      questionnaireType ||
      (this.formGroup.controls['type'] && this.formGroup.controls['type'].value);
    const schema = this.resolveSchemaForType(resolvedType);

    const needsClinicianPrefill = consentFormRequiresClinicianInput(schema);
    const hasCachedPrefill = Object.prototype.hasOwnProperty.call(
      this.clinicianPrefillsByType,
      resolvedType
    );

    // Multi-send preflight already collected prefills. Only open the dialog here for
    // paths that did not preflight (e.g. single-questionnaire send).
    const afterClinicianPrefill$ =
      needsClinicianPrefill && !hasCachedPrefill
        ? this.consentSendService.openClinicianPrefillDialog(
            schema,
            this.getDefaultPractitionerId(),
            this.getConsentPrefillDisplayName(resolvedType, schema)
          )
        : of(
            hasCachedPrefill
              ? ({
                  clinicianPrefill: this.clinicianPrefillsByType[resolvedType]
                } as ConsentClinicianPrefillDialogResult)
              : ({ clinicianPrefill: null } as ConsentClinicianPrefillDialogResult)
          );

    afterClinicianPrefill$
      .pipe(
        switchMap((clinicianPrefillResult: ConsentClinicianPrefillDialogResult | null) => {
          if (needsClinicianPrefill && !hasCachedPrefill && !clinicianPrefillResult) {
            this.abortPendingSend();
            return of(null);
          }

          const questionnaireRequestInput = this.questionnaireSendBuilderService.applyQuestionnaireSchemaToInput(
            {
              ...this.formGroup.value,
              language: this.languageFormControl.value,
              ...(canSendAsMultiQuestionnaire(resolvedType) && { groupId })
            },
            resolvedType,
            clinicianPrefillResult ? clinicianPrefillResult.clinicianPrefill : null
          );

          if (this.data.massClinicPatient) {
            const questionnaireId = uuid();
            const {
              id,
              massClinicPatientClinicId,
              ...massClinicPatient
            } = this.data.massClinicPatient;

            const updateMassClinicPatient: UpdateMassClinicPatientInput = {
              id,
              massClinicPatientClinicId,
              massClinicPatientMassClinicId: massClinicPatient.massClinic.id,
              massClinicPatientPatientId: massClinicPatient.patient.id,
              massClinicPatientQuestionnaireRequestId: questionnaireId
            };

            return this.questionnaireRequestService.createQuestionnaireRequestAndUpdateMassClinicPatient(
              {
                id: questionnaireId,
                questionnaireRequestMassClinicPatientId: id,
                ...questionnaireRequestInput
              },
              updateMassClinicPatient
            );
          }

          return this.questionnaireRequestService.createQuestionnaireRequest(
            questionnaireRequestInput
          );
        })
      )
      .subscribe(
        (questionnaireRequest: QuestionnaireRequest) => {
          if (!questionnaireRequest) {
            return;
          }
          this.logger.debug('Submitted Questionnaire Request: ', questionnaireRequest);
          this.questionnaireRequestsSent.push(questionnaireRequest);
          this.patientService.questionnaires = [...this.patientService.questionnaires];
          if (this.questionnairesToBeSent.length === 0) {
            this.dispatchSelectedPampTodoForPatient().subscribe(
              () => {
                this.sending = false;
                this.dialogRef.close(
                  this.questionnaireRequestsSent.length > 1
                    ? this.questionnaireRequestsSent
                    : this.questionnaireRequestsSent[0]
                );
                this.openSuccessSnackbar();
              },
              () => {
                this.sending = false;
              }
            );
          } else {
            this.createQuestionnaireRequest(this.questionnairesToBeSent.pop(), groupId);
          }
        },
        (error: ApolloError) => {
          this.sending = false;
          this.errorHandlerService.handleGraphQLError(error);
        }
      );
  }

  private getDefaultPractitionerId(): string {
    return (this.staffService.staff && this.staffService.staff.id) || '';
  }

  public updateQuestionnaireType(questionnaireType?: string) {
    this.sending = true;
    if (questionnaireType) {
      this.formGroup.controls['type'].patchValue(questionnaireType);
    }
    this.questionnaireRequestService
      .updateQuestionnaireType(this.data.questionnaireRequest, this.formGroup.controls.type.value)
      .subscribe(
        (questionnaireRequest: QuestionnaireRequest) => {
          this.questionnaireRequestsSent.push(questionnaireRequest);
          if (this.questionnairesToBeSent.length === 0) {
            this.dispatchSelectedPampTodoForPatient().subscribe(
              () => {
                this.sending = false;
                this.dialogRef.close(
                  this.questionnaireRequestsSent.length > 1
                    ? this.questionnaireRequestsSent
                    : this.questionnaireRequestsSent[0]
                );
                this.openSuccessSnackbar();
              },
              () => {
                this.sending = false;
              }
            );
          } else {
            this.createQuestionnaireRequest(this.questionnairesToBeSent.pop());
          }
        },
        (error: ApolloError) => {
          this.sending = false;
          this.errorHandlerService.handleGraphQLError(error);
        }
      );
  }

  /** Names everything the send actually delivered: questionnaires and Patient App todos alike. */
  private openSuccessSnackbar() {
    const sentNames = this.questionnaireRequestsSent
      .map(questionnaireRequest => this.getQuestionnaireTypeFriendlyName(questionnaireRequest))
      .concat(this.pampTodoNamesSent);

    if (sentNames.length === 0) {
      return;
    }

    const dismissText = this.translocoService.translate('Dismiss') || 'Dismiss';

    if (sentNames.length > 1) {
      const heading =
        this.translocoService.translate('The following questionnaires have been sent:') ||
        'The following questionnaires have been sent:';
      const message = sentNames.reduce((text, name) => `${text} [${name}]`, heading);

      this.snackBar.open(message, dismissText, {
        duration: 7000
      });
    } else {
      this.snackBar.open(`Sent ${sentNames[0]}`, dismissText, {
        duration: 4000
      });
    }
  }

  private getQuestionnaireTypeFriendlyName(questionnaireRequest: QuestionnaireRequest) {
    if (questionnaireRequest.type === QuestionnaireType.CUSTOM) {
      const config = safeJsonParse(questionnaireRequest.config);
      if (config.schema && config.schema.name) {
        return config.schema.name;
      }
      return 'CUSTOM';
    }
    return this.questionnaireRequestService.questionnaireTypeToFriendlyNameMap[
      questionnaireRequest.type
    ];
  }

  /** Display name for clinician-prefill dialog; falls back safely if maps are incomplete. */
  private getConsentPrefillDisplayName(
    questionnaireType: string,
    schema?: FormTemplateSchema
  ): string {
    if (schema && schema.name && String(schema.name).trim()) {
      return String(schema.name).trim();
    }
    return (
      (this.consentFormKeyToFriendlyNameMap &&
        this.consentFormKeyToFriendlyNameMap[questionnaireType]) ||
      (this.questionnaireTypeToFriendlyNameMap &&
        this.questionnaireTypeToFriendlyNameMap[questionnaireType]) ||
      (this.questionnaireRequestService.customQuestionnaireKeyToFriendlyNameMap &&
        this.questionnaireRequestService.customQuestionnaireKeyToFriendlyNameMap[
          questionnaireType
        ]) ||
      questionnaireType ||
      ''
    );
  }
}
